import { Table as AntdTable, TableProps } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserContext } from '@/contexts/UserContext';

type AppTableProps<RecordType extends object = any> = TableProps<RecordType> & {
  /** Usa el valor persistido en localStorage como pageSize por defecto */
  persistPageSize?: boolean;
};

const DEFAULT_PAGE_SIZE_OPTIONS = [20, 40, 60, 80];
const Table = <RecordType extends object = any>({
  persistPageSize = true,
  pagination,
  onChange,
  ...rest
}: AppTableProps<RecordType>) => {
  const { pageSizePreference, setPageSizePreference } = useUserContext();
  const { t } = useTranslation();
  const defaultShowTotal = useCallback(
    (total: number, range: [number, number]) => `${range[0]} - ${range[1]} ${t('de')} ${total}`,
    [t],
  );
  // paginación default
  const mergedPagination = useMemo(() => {
    if (pagination === false) return pagination;

    const basePagination = typeof pagination === 'object' ? pagination : {};

    return {
      ...basePagination,
      pageSize: persistPageSize ? pageSizePreference : basePagination?.pageSize,
      pageSizeOptions: basePagination?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS,
      showSizeChanger: basePagination?.showSizeChanger ?? true,
      showTotal: basePagination?.showTotal ?? defaultShowTotal,
    };
  }, [defaultShowTotal, pagination, persistPageSize, pageSizePreference]);
  // Cambio de numero de elementos por página
  const handleChange: typeof onChange = useCallback(
    (pag, filters, sorter, extra) => {
      onChange?.(pag, filters, sorter, extra);

      if (persistPageSize && pag?.pageSize && pag.pageSize !== pageSizePreference) {
        setPageSizePreference(pag.pageSize);
      }
    },
    [onChange, pageSizePreference, persistPageSize, setPageSizePreference],
  );

  return <AntdTable<RecordType> {...rest} pagination={mergedPagination} onChange={handleChange} />;
};

type AntdTableType = typeof AntdTable;
type AppTableComponent = typeof Table & {
  Column: AntdTableType['Column'];
  ColumnGroup: AntdTableType['ColumnGroup'];
  Summary: AntdTableType['Summary'];
};

const AppTable = Table as AppTableComponent;

AppTable.Column = AntdTable.Column;
AppTable.ColumnGroup = AntdTable.ColumnGroup;
AppTable.Summary = AntdTable.Summary;

export default AppTable;
